import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { ReplaySubject } from 'rxjs';
import { env } from '../../environments/environment';
import { UserService } from '../user/user.service';
import { SegmentService } from 'ngx-segment-analytics';

const IMPERSONATED_TOKEN_KEY = 'impersonated-auth-token';
const TOKEN_KEY = 'auth-token';
const TOKEN_EXP = 'auth-token-exp';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  authenticationState = new ReplaySubject<boolean>(1);

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private segment: SegmentService,
  ) {
    this.checkToken();
  }

  get expiresIn(): number {
    return parseInt(localStorage.getItem(TOKEN_EXP), 10);
  }
  set expiresIn(new_exp: number) {
    if (new_exp == null) {
      localStorage.removeItem(TOKEN_EXP);
    } else {
      localStorage.setItem(TOKEN_EXP, new_exp.toString());
    }
  }

  get token(): string {
    return localStorage.getItem(TOKEN_KEY);
  }
  set token(new_token: string) {
    if (new_token == null) {
      localStorage.removeItem(TOKEN_KEY);
    } else {
      localStorage.setItem(TOKEN_KEY, new_token);
    }
  }

  get impersonatedToken(): string {
    return localStorage.getItem(IMPERSONATED_TOKEN_KEY);
  }
  set impersonatedToken(new_token: string) {
    if (new_token == null) {
      localStorage.removeItem(IMPERSONATED_TOKEN_KEY);
    } else {
      localStorage.setItem(IMPERSONATED_TOKEN_KEY, new_token);
    }
  }

  checkToken() {
    if (this.token) {
      this.authenticationState.next(true);
    } else {
      this.authenticationState.next(false);
    }
  }

  async impersonate(org: { id?: number }) {
    try {
      const response = await this.http.post<any>(`${env.APIUrl}/auth/impersonate`, org).toPromise();
      if (response?.jwt) {
        this.impersonatedToken = response.jwt;
        location.href = '/';
      }
      return false;
    } catch (e) {
      return e;
    }
  }

  async leaveImpersonation() {
    this.impersonatedToken = null;
    location.href = '/';
  }

  async renew() {
    try {
      // console.log('renewing with token', this.token);
      const response = await this.http.get<any>(`${env.APIUrl}/auth/renew`).toPromise();
      if (response?.jwt) {
        if (this.impersonatedToken) {
          this.impersonatedToken = response.jwt;
        } else {
          this.token = response.jwt;
        }
        this.expiresIn = response.expiresIn;
        this.authenticationState.next(true);

        // console.log('setting renew', this.expiresIn - 10);
        setTimeout(() => this.renew(), (this.expiresIn - 10) * 1000);
      }
    } catch (e) {
      console.log(e);
    }
  }

  async recover(info: { token: string; newPassword: string }) {
    return await this.http.post<any>(`${env.APIUrl}/auth/recover`, info).toPromise();
  }

  async forgot(email: string) {
    return await this.http.get<any>(`${env.APIUrl}/auth/recover`, { params: { email } }).toPromise();
  }

  async tokenInfo(token: string) {
    return await this.http.get<{ error?: string; email?: string }>(`${env.APIUrl}/auth/token/${token}`).toPromise();
  }

  async login(credentials: any) {
    try {
      const response = await this.http.post<any>(`${env.APIUrl}/auth/login`, credentials).toPromise();
      if (response?.jwt) {
        this.token = response.jwt;
        this.expiresIn = response.expiresIn;
        this.authenticationState.next(true);
        if (response.type == 'AdminUser') {
          const profile = await this.userService.profile();

          this.segment.identify(profile.id, {
            email: profile.email,
            fullname: profile.name,
          });
          this.segment.group(profile.organization.id.toString(), { userId: profile.id });
        }

        setTimeout(() => this.renew(), (this.expiresIn - 10) * 1000);

        return true;
      }
      return false;
    } catch (e) {
      return e;
    }
  }
  async loginWithGoogle(code: string) {
    try {
      const response = await this.http.get<any>(`${env.APIUrl}/auth/google/code`, { params: { code } }).toPromise();
      console.log({ response });
      if (response?.jwt) {
        this.token = response.jwt;
        this.expiresIn = response.expiresIn;
        this.authenticationState.next(true);
        if (response.type == 'AdminUser') {
          const profile = await this.userService.profile();

          this.segment.identify(profile.id, {
            email: profile.email,
            fullname: profile.name,
          });
          this.segment.group(profile.organization.id.toString(), { userId: profile.id });
        }

        setTimeout(() => this.renew(), (this.expiresIn - 10) * 1000);

        return true;
      }
      return false;
    } catch (e) {
      return e;
    }
  }

  logout() {
    this.token = null;
    this.impersonatedToken = null;
    this.authenticationState.next(false);
    this.userService.cachedProfile = null;
    this.userService.clearCache();
    Sentry.setUser(null);
  }

  get isAuthenticated() {
    return this.authenticationState.asObservable();
  }

  checkPasswordStrength(password: string) {
    if (password.length < 10) {
      return false;
    }

    let typeCount = 0;

    // Has lowercase
    if (password.toUpperCase() !== password) {
      typeCount++;
    }

    // Has uppercase
    if (password.toLowerCase() !== password) {
      typeCount++;
    }

    // Has number
    if (/\d/.test(password)) {
      typeCount++;
    }

    // Has special characters (Any printable ASCII chars other than space, letters and digits)
    if (/[!-\/:-@[-`{-~]/.test(password)) {
      typeCount++;
    }

    return typeCount >= 3;
  }
}
